/**
 * While a game is in the air, show the rest of its two teams' weekend.
 *
 * Mike, 22 September: "whenever we do a drag and drop, to be able to ... right
 * now it's really kind of hard to see where else 17U Demo 5 and 14 play."
 *
 * This is sight, not rules. Nothing here decides whether a drop is allowed —
 * it only works out which other placed games those two teams are in, which
 * time rows those games sit in, and how many of them are on a day the operator
 * is not looking at. It is pure, so it can be tested without a board.
 */

export type CompanionGame = {
  id: string;
  team_a_registration_id?: string | null;
  team_b_registration_id?: string | null;
  team_a_name?: string | null;
  team_b_name?: string | null;
  scheduled_date?: string | null;
  scheduled_start_time?: string | null;
  court_id?: string | null;
};

export type CompanionMark = {
  /** Ids of the other placed games either team is in. */
  gameIds: Set<string>;
  /** "<date>|<startMinutes>" for every row one of those games sits in. */
  rowKeys: Set<string>;
  /** date → how many marked games are on it, in board order. */
  byDate: Map<string, number>;
  /** The two team names, in order. */
  teams: string[];
  total: number;
  /** What a screen reader hears, and what the board says in words. */
  message: string;
};

/** Minutes past midnight for "HH:MM[:SS]", or null. */
const minutesOf = (t?: string | null): number | null => {
  if (!t) return null;
  const [h, m] = String(t).split(':');
  const n = Number(h) * 60 + Number(m);
  return Number.isFinite(n) ? n : null;
};

/** registration id → every game that team is in. Built once per board load. */
export function buildTeamGameIndex(games: CompanionGame[]): Map<string, CompanionGame[]> {
  const m = new Map<string, CompanionGame[]>();
  for (const g of games) {
    for (const t of [g.team_a_registration_id, g.team_b_registration_id]) {
      if (!t) continue;
      const list = m.get(t);
      if (list) list.push(g);
      else m.set(t, [g]);
    }
  }
  return m;
}

/**
 * Everything to mark for the game being dragged. Returns null when the game is
 * unknown or its teams play nothing else — the board then marks nothing at all
 * rather than flashing an empty treatment on and off.
 */
export function companionMark(
  index: Map<string, CompanionGame[]>,
  dragged: CompanionGame | null | undefined,
  dayLabel: (date: string) => string,
): CompanionMark | null {
  if (!dragged) return null;
  const teamIds = [dragged.team_a_registration_id, dragged.team_b_registration_id]
    .filter(Boolean) as string[];
  if (teamIds.length === 0) return null;

  const gameIds = new Set<string>();
  const rowKeys = new Set<string>();
  const byDate = new Map<string, number>();
  const dates: string[] = [];

  for (const t of teamIds) {
    for (const g of index.get(t) ?? []) {
      if (g.id === dragged.id || gameIds.has(g.id)) continue;
      const start = minutesOf(g.scheduled_start_time);
      if (!g.scheduled_date || start == null || !g.court_id) continue; // unplaced: nothing to point at
      gameIds.add(g.id);
      rowKeys.add(`${g.scheduled_date}|${start}`);
      if (!byDate.has(g.scheduled_date)) dates.push(g.scheduled_date);
      byDate.set(g.scheduled_date, (byDate.get(g.scheduled_date) ?? 0) + 1);
    }
  }
  if (gameIds.size === 0) return null;

  dates.sort();
  const ordered = new Map<string, number>(dates.map((d) => [d, byDate.get(d)!]));
  const teams = [dragged.team_a_name, dragged.team_b_name].filter(Boolean) as string[];
  const total = gameIds.size;
  const per = dates.map((d) => `${ordered.get(d)} on ${dayLabel(d)}`);
  const where = per.length === 1 ? per[0]
    : `${per.slice(0, -1).join(', ')} and ${per[per.length - 1]}`;
  const message = `${teams.join(' and ')} also play ${total} other ${
    total === 1 ? 'game' : 'games'}, ${where}.`;

  return { gameIds, rowKeys, byDate: ordered, teams, total, message };
}
